// ============= Imports =============
import { createRelayHubLog } from '../shared/utils';
import type { RelayHubHandlers } from '../shared';
import { AskRelayHubEvents } from './events';
import type { AskRelayHubOptions } from './types';
import type {
  AskSessionMessageCompletionPayload,
  AskSessionMessageStreamPayload,
  AskSessionThreadSynopsisPayload,
} from './payloads';

// ============= Handler Factory =============

/**
 * Creates a full set of Ask relay hub handlers and subscribe/unsubscribe helpers.
 * Normalizes SignalR arguments, filters by workspaceId/askSessionId, logs to console and toast history,
 * and invokes optional callbacks. Uses shared logging utility.
 */
export const createAskRelayHubHandlers = (
  handlers: RelayHubHandlers,
  options: AskRelayHubOptions = {}
) => {
  const {
    askSessionId,
    onDataRefresh,
    onMessageReceived,
    onEventReceived,
    enableLogging = true,
  } = options;

  const log = createRelayHubLog('AskRelayHub', enableLogging, '💬');

  const createAskEventHandler = (
    event: AskRelayHubEvents,
    getParsedData: (param1: any, param2?: any, param3?: any) => any
  ) => {
    return (param1: any, param2?: any, param3?: any) => {
      const parsed = getParsedData(param1, param2, param3);

      if (askSessionId && parsed.askSessionId && parsed.askSessionId !== askSessionId) {
        return;
      }

      log(event, {
        param1,
        param2,
        param3,
        parsed,
        askSessionIdFilter: askSessionId,
      });

      if (onEventReceived) {
        onEventReceived(event, { param1, param2, param3, ...parsed }, parsed);
      }
      if (onMessageReceived) {
        onMessageReceived({ event, ...parsed });
      }
      if (onDataRefresh) void onDataRefresh();
    };
  };

  const handleAskSessionMessageReceived = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_RECIEVED,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualMessageData: any;
      if (param3 === undefined && typeof param2 === 'string') {
        if (typeof param1 === 'string' && param1.includes('-')) {
          actualAskSessionId = param1;
          actualMessageData = param2;
          actualWorkspaceId = 'unknown';
        } else {
          actualWorkspaceId = typeof param1 === 'string' ? param1 : 'unknown';
          actualMessageData = param2;
          actualAskSessionId = 'unknown';
        }
      } else {
        actualWorkspaceId =
          (typeof param2 === 'string' ? param2 : undefined) ||
          param1?.workspaceId ||
          param1?.workspace?.id ||
          'unknown';
        actualAskSessionId =
          (typeof param3 === 'string' ? param3 : undefined) ||
          (typeof param2 === 'string' ? param2 : undefined) ||
          param1?.askSessionId ||
          param1?.sessionId ||
          param1?.id ||
          'unknown';
        actualMessageData = param3 || param2 || param1;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        messageData: actualMessageData,
        messageId: actualMessageData?.messageId || actualMessageData?.id || 'unknown',
        param1,
      };
    }
  );

  const handleAskSessionMessageCompletion = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_COMPLETION,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualCompletionData: AskSessionMessageCompletionPayload | any;
      // Server may send [messageObject] or [messageObject, statusString]
      const firstArg = Array.isArray(param1) ? param1[0] : param1;
      if (firstArg && typeof firstArg === 'object' && firstArg.id) {
        actualCompletionData = firstArg as AskSessionMessageCompletionPayload;
        actualAskSessionId = firstArg.askSessionId || 'unknown';
        actualWorkspaceId = firstArg.workspaceId || 'unknown';
      } else if (param1 && typeof param1 === 'object' && param1.id) {
        actualCompletionData = param1 as AskSessionMessageCompletionPayload;
        actualAskSessionId = param1.askSessionId || 'unknown';
        actualWorkspaceId = param1.workspaceId || 'unknown';
      } else if (param3 === undefined && typeof param2 === 'string') {
        if (typeof param1 === 'string' && param1.includes('-')) {
          actualAskSessionId = param1;
          actualCompletionData = param2;
          actualWorkspaceId = 'unknown';
        } else {
          actualWorkspaceId = typeof param1 === 'string' ? param1 : 'unknown';
          actualCompletionData = param2;
          actualAskSessionId = 'unknown';
        }
      } else {
        const param1Obj = typeof param1 === 'object' && param1 !== null ? param1 : undefined;
        actualWorkspaceId =
          (typeof param2 === 'string' ? param2 : undefined) ||
          param1Obj?.workspaceId ||
          param1Obj?.workspace?.id ||
          'unknown';
        actualAskSessionId =
          (typeof param3 === 'string' ? param3 : undefined) ||
          (typeof param2 === 'string' ? param2 : undefined) ||
          param1Obj?.askSessionId ||
          param1Obj?.sessionId ||
          'unknown';
        actualCompletionData = (param3 || param2 || param1) as AskSessionMessageCompletionPayload;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        completionData: actualCompletionData,
        messageId: actualCompletionData?.messageId || actualCompletionData?.id || 'unknown',
        messageData: actualCompletionData,
        param1,
      };
    }
  );

  const handleAskMessageDocuments = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_DOCUMENTS,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualMessageId: string;
      let actualDocumentsData: any;
      if (param3 === undefined && typeof param2 === 'string' && typeof param1 === 'string') {
        actualWorkspaceId = 'unknown';
        actualAskSessionId = param1;
        actualMessageId = 'unknown';
        actualDocumentsData = { status: param2 };
      } else if (param3 === undefined && typeof param2 === 'object') {
        actualWorkspaceId = param1?.workspaceId || param2?.workspaceId || 'unknown';
        actualAskSessionId = param1?.askSessionId || param2?.askSessionId || 'unknown';
        actualMessageId = param1?.messageId || param2?.messageId || 'unknown';
        actualDocumentsData = param2 || param1;
      } else {
        actualWorkspaceId = param2 || param1?.workspaceId || param1?.workspace?.id || 'unknown';
        actualAskSessionId =
          param3 || param2 || param1?.askSessionId || param1?.sessionId || 'unknown';
        actualMessageId = param3?.messageId || param2?.messageId || param1?.messageId || 'unknown';
        actualDocumentsData = param3 || param2 || param1;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        messageId: actualMessageId,
        documentsData: actualDocumentsData,
        documents: actualDocumentsData?.documents || actualDocumentsData || [],
        param1,
      };
    }
  );

  const handleAskMessageDocumentContext = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_DOCUMENT_CONTEXT,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualMessageId: string;
      let actualContextData: any;
      if (param3 === undefined && typeof param2 === 'string' && typeof param1 === 'string') {
        actualWorkspaceId = 'unknown';
        actualAskSessionId = param1;
        actualMessageId = 'unknown';
        actualContextData = { status: param2 };
      } else if (param3 === undefined && typeof param2 === 'object') {
        actualWorkspaceId = param1?.workspaceId || param2?.workspaceId || 'unknown';
        actualAskSessionId = param1?.askSessionId || param2?.askSessionId || 'unknown';
        actualMessageId = param1?.messageId || param2?.messageId || 'unknown';
        actualContextData = param2 || param1;
      } else {
        actualWorkspaceId = param2 || param1?.workspaceId || param1?.workspace?.id || 'unknown';
        actualAskSessionId =
          param3 || param2 || param1?.askSessionId || param1?.sessionId || 'unknown';
        actualMessageId = param3?.messageId || param2?.messageId || param1?.messageId || 'unknown';
        actualContextData = param3 || param2 || param1;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        messageId: actualMessageId,
        contextData: actualContextData,
        param1,
      };
    }
  );

  const handleAskMessageIntent = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_INTENT,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualMessageId: string;
      let actualIntentData: any;
      if (param3 === undefined && typeof param2 === 'string' && typeof param1 === 'string') {
        actualWorkspaceId = 'unknown';
        actualAskSessionId = param1;
        actualMessageId = 'unknown';
        actualIntentData = { status: param2 };
      } else if (param3 === undefined && typeof param2 === 'object') {
        actualWorkspaceId = param1?.workspaceId || param2?.workspaceId || 'unknown';
        actualAskSessionId = param1?.askSessionId || param2?.askSessionId || 'unknown';
        actualMessageId = param1?.messageId || param2?.messageId || 'unknown';
        actualIntentData = param2 || param1;
      } else {
        actualWorkspaceId = param2 || param1?.workspaceId || param1?.workspace?.id || 'unknown';
        actualAskSessionId =
          param3 || param2 || param1?.askSessionId || param1?.sessionId || 'unknown';
        actualMessageId = param3?.messageId || param2?.messageId || param1?.messageId || 'unknown';
        actualIntentData = param3 || param2 || param1;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        messageId: actualMessageId,
        intentData: actualIntentData,
        intent: actualIntentData?.intent || actualIntentData?.type || 'unknown',
        param1,
      };
    }
  );

  const handleAskMessageTokens = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_TOKENS,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualMessageId: string;
      let actualTokensData: any;
      if (param3 === undefined && typeof param2 === 'object') {
        actualWorkspaceId = param1?.workspaceId || param2?.workspaceId || 'unknown';
        actualAskSessionId = param1?.askSessionId || param2?.askSessionId || 'unknown';
        actualMessageId = param1?.messageId || param2?.messageId || 'unknown';
        actualTokensData = param2 || param1;
      } else {
        actualWorkspaceId = param2 || param1?.workspaceId || param1?.workspace?.id || 'unknown';
        actualAskSessionId =
          param3 || param2 || param1?.askSessionId || param1?.sessionId || 'unknown';
        actualMessageId = param3?.messageId || param2?.messageId || param1?.messageId || 'unknown';
        actualTokensData = param3 || param2 || param1;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        messageId: actualMessageId,
        tokensData: actualTokensData,
        tokens: actualTokensData?.tokens || actualTokensData?.count || 0,
        param1,
      };
    }
  );

  const handleAskMessageAudience = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_AUDIENCE,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualMessageId: string;
      let actualAudienceData: any;
      if (param3 === undefined && typeof param2 === 'string' && typeof param1 === 'string') {
        actualWorkspaceId = 'unknown';
        actualAskSessionId = param1;
        actualMessageId = 'unknown';
        actualAudienceData = { status: param2 };
      } else if (param3 === undefined && typeof param2 === 'object') {
        actualWorkspaceId = param1?.workspaceId || param2?.workspaceId || 'unknown';
        actualAskSessionId = param1?.askSessionId || param2?.askSessionId || 'unknown';
        actualMessageId = param1?.messageId || param2?.messageId || 'unknown';
        actualAudienceData = param2 || param1;
      } else {
        actualWorkspaceId = param2 || param1?.workspaceId || param1?.workspace?.id || 'unknown';
        actualAskSessionId =
          param3 || param2 || param1?.askSessionId || param1?.sessionId || 'unknown';
        actualMessageId = param3?.messageId || param2?.messageId || param1?.messageId || 'unknown';
        actualAudienceData = param3 || param2 || param1;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        messageId: actualMessageId,
        audienceData: actualAudienceData,
        audience: actualAudienceData?.audience || actualAudienceData?.target || 'unknown',
        param1,
      };
    }
  );

  const handleAskMessageExternal = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_EXTERNAL,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualMessageId: string;
      let actualExternalData: any;
      if (param3 === undefined && typeof param2 === 'object') {
        actualWorkspaceId = param1?.workspaceId || param2?.workspaceId || 'unknown';
        actualAskSessionId = param1?.askSessionId || param2?.askSessionId || 'unknown';
        actualMessageId = param1?.messageId || param2?.messageId || 'unknown';
        actualExternalData = param2 || param1;
      } else {
        actualWorkspaceId = param2 || param1?.workspaceId || param1?.workspace?.id || 'unknown';
        actualAskSessionId =
          param3 || param2 || param1?.askSessionId || param1?.sessionId || 'unknown';
        actualMessageId = param3?.messageId || param2?.messageId || param1?.messageId || 'unknown';
        actualExternalData = param3 || param2 || param1;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        messageId: actualMessageId,
        externalData: actualExternalData,
        param1,
      };
    }
  );

  const handleAskThreadSynopsis = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_THREAD_SYNOPSIS,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualSynopsisData: AskSessionThreadSynopsisPayload | any;
      if (param1 && typeof param1 === 'object' && param1.id) {
        actualSynopsisData = param1 as AskSessionThreadSynopsisPayload;
        actualAskSessionId = param1.id;
        actualWorkspaceId = param1.workspaceId || 'unknown';
      } else if (param3 === undefined && typeof param2 === 'object') {
        actualWorkspaceId = param1?.workspaceId || param2?.workspaceId || 'unknown';
        actualAskSessionId = param1?.askSessionId || param2?.askSessionId || 'unknown';
        actualSynopsisData = (param2 || param1) as AskSessionThreadSynopsisPayload;
      } else {
        actualWorkspaceId =
          param2 || param1?.workspaceId || param1?.workspace?.id || 'unknown';
        actualAskSessionId =
          param3 ||
          param2 ||
          param1?.askSessionId ||
          param1?.sessionId ||
          param1?.id ||
          'unknown';
        actualSynopsisData = (param3 || param2 || param1) as AskSessionThreadSynopsisPayload;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        synopsisData: actualSynopsisData,
        synopsis:
          actualSynopsisData?.askThreadSynopsis ||
          actualSynopsisData?.synopsis ||
          actualSynopsisData?.summary ||
          'unknown',
        param1,
      };
    }
  );

  const handleAskMessageStream = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_STREAM,
    (param1: any, param2?: any, param3?: any) => {
      let actualWorkspaceId: string;
      let actualAskSessionId: string;
      let actualMessageId: string;
      let actualStreamData: AskSessionMessageStreamPayload | any;
      if (param1 && typeof param1 === 'object' && param1.id) {
        actualStreamData = param1 as AskSessionMessageStreamPayload;
        actualMessageId = param1.id;
        actualAskSessionId = param1.askSessionId || 'unknown';
        actualWorkspaceId = param1.workspaceId || 'unknown';
      } else if (param3 === undefined && typeof param2 === 'object') {
        actualWorkspaceId = param1?.workspaceId || param2?.workspaceId || 'unknown';
        actualAskSessionId = param1?.askSessionId || param2?.askSessionId || 'unknown';
        actualMessageId = param1?.messageId || param2?.messageId || param2?.id || 'unknown';
        actualStreamData = (param2 || param1) as AskSessionMessageStreamPayload;
      } else {
        const param1Obj = typeof param1 === 'object' && param1 !== null ? param1 : undefined;
        actualWorkspaceId =
          (typeof param2 === 'string' ? param2 : undefined) ||
          param1Obj?.workspaceId ||
          param1Obj?.workspace?.id ||
          'unknown';
        actualAskSessionId =
          (typeof param3 === 'string' ? param3 : undefined) ||
          (typeof param2 === 'string' ? param2 : undefined) ||
          param1Obj?.askSessionId ||
          param1Obj?.sessionId ||
          'unknown';
        actualMessageId =
          param3?.messageId ||
          param3?.id ||
          param2?.messageId ||
          param2?.id ||
          param1Obj?.messageId ||
          param1Obj?.id ||
          'unknown';
        actualStreamData = (param3 || param2 || param1) as AskSessionMessageStreamPayload;
      }
      return {
        workspaceId: actualWorkspaceId,
        askSessionId: actualAskSessionId,
        messageId: actualMessageId,
        streamData: actualStreamData,
        streamContent: actualStreamData?.content || '',
        messageData: actualStreamData,
        param1,
      };
    }
  );

  const handleUserConnectedToAskSession = (
    workspaceIdParam: string,
    askSessionIdParam: string,
    userId: string
  ) => {
    if (askSessionId && askSessionIdParam !== askSessionId) return;

    const parsed = {
      workspaceId: workspaceIdParam || 'unknown',
      askSessionId: askSessionIdParam || 'unknown',
      userId: userId || 'unknown',
    };

    log(AskRelayHubEvents.USER_CONNECTED_TO_ASK_SESSION, {
      workspaceId: workspaceIdParam,
      askSessionId: askSessionIdParam,
      userId,
    });

    if (onEventReceived) {
      onEventReceived(
        AskRelayHubEvents.USER_CONNECTED_TO_ASK_SESSION,
        { workspaceId: workspaceIdParam, askSessionId: askSessionIdParam, userId },
        parsed
      );
    }
    if (onMessageReceived) {
      onMessageReceived({ event: AskRelayHubEvents.USER_CONNECTED_TO_ASK_SESSION, ...parsed });
    }
    if (onDataRefresh) void onDataRefresh();
  };

  const handleAskMessageStreamStarted = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_STREAM_STARTED,
    (param1: any, param2?: any, param3?: any) => {
      const obj = param1 && typeof param1 === 'object' ? param1 : param2 || param3;
      return {
        workspaceId: obj?.workspaceId || param1?.workspaceId || 'unknown',
        askSessionId: obj?.askSessionId || param1?.askSessionId || 'unknown',
        messageId: obj?.messageId || obj?.id || param1?.messageId || param1?.id,
        messageData: obj,
        param1,
      };
    },
  );

  const handleAskMessageStreamEnded = createAskEventHandler(
    AskRelayHubEvents.ASK_SESSION_MESSAGE_STREAM_ENDED,
    (param1: any, param2?: any, param3?: any) => {
      const obj = param1 && typeof param1 === 'object' ? param1 : param2 || param3;
      return {
        workspaceId: obj?.workspaceId || param1?.workspaceId || 'unknown',
        askSessionId: obj?.askSessionId || param1?.askSessionId || 'unknown',
        messageId: obj?.messageId || obj?.id || param1?.messageId || param1?.id,
        messageData: obj,
        param1,
      };
    }
  );

  const eventHandlers: Array<[string, (...args: any[]) => void]> = [
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_RECIEVED, handleAskSessionMessageReceived],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_COMPLETION, handleAskSessionMessageCompletion],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_DOCUMENTS, handleAskMessageDocuments],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_DOCUMENT_CONTEXT, handleAskMessageDocumentContext],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_INTENT, handleAskMessageIntent],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_TOKENS, handleAskMessageTokens],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_AUDIENCE, handleAskMessageAudience],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_EXTERNAL, handleAskMessageExternal],
    [AskRelayHubEvents.ASK_SESSION_THREAD_SYNOPSIS, handleAskThreadSynopsis],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_STREAM, handleAskMessageStream],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_STREAM_STARTED, handleAskMessageStreamStarted],
    [AskRelayHubEvents.ASK_SESSION_MESSAGE_STREAM_ENDED, handleAskMessageStreamEnded],
    [AskRelayHubEvents.USER_CONNECTED_TO_ASK_SESSION, handleUserConnectedToAskSession],
  ];

  const subscribe = () => {
    eventHandlers.forEach(([event, handler]) => handlers.on(event, handler));
  };
  const unsubscribe = () => {
    eventHandlers.forEach(([event, handler]) => handlers.off(event, handler));
  };

  return {
    subscribe,
    unsubscribe,
    handlers: {
      handleAskSessionMessageReceived,
      handleAskSessionMessageCompletion,
      handleAskMessageDocuments,
      handleAskMessageDocumentContext,
      handleAskMessageIntent,
      handleAskMessageTokens,
      handleAskMessageAudience,
      handleAskMessageExternal,
      handleAskThreadSynopsis,
      handleAskMessageStream,
      handleUserConnectedToAskSession,
      handleAskMessageStreamStarted,
      handleAskMessageStreamEnded,
    },
  };
}
